import { useRef, useState } from "react";
import useFilterStorage from "./useFilterStorage";

type FilterType = "in" | "like";
export type Filters = {
  [key: string]: string;
};

export function useInitFilters(key: string = "") {
  const { filters: localStorageFilters } = useFilterStorage(key);
  const initFilters = useRef<Filters>(localStorageFilters);

  const getInitFilterValue = (key: string, operator: FilterType = "in") => {
    const filter = initFilters.current[key] || "";
    if (operator === "like") {
      return filter.split("'")[1];
    }
    if (filter.includes(operator)) {
      return filter.split("(")[1].split(")")[0];
    }
  };

  const getInitFilterValues = (key: string): number[] => {
    const filter = initFilters.current[key] || "";
    if (filter.includes("in")) {
      return filter
        .split("(")[1]
        .split(")")[0]
        .split(",")
        .map((x) => parseInt(x.replace(/'/g, "")));
    }
    return [];
  };

  return {
    initFilters: initFilters.current,
    getInitFilterValue,
    getInitFilterValues,
  };
}

export function useFilters(key: string = "") {
  const { filters, setFilters, saveFilters, clearFilters } =
    useFilterStorage(key);

  const [searchPhrase, setSearchPhrase] = useState("");
  const { initFilters, getInitFilterValue, getInitFilterValues } =
    useInitFilters(key);

  const changeFilter = (key: string, filter: string) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      newFilters[key] = filter;
      saveFilters(newFilters);
      return newFilters;
    });
  };

  const updateAllFilters = (filters: { key: string; value: string }[]) => {
    clearFilters();
    const newFilters = {} as Filters;
    filters.forEach((filter) => {
      newFilters[filter.key] = filter.value;
    });
    setFilters(newFilters);
    saveFilters(newFilters);
  };

  const joinNotEmptyFiltersWithAnd = () => {
    return Object.entries(filters)
      .filter(([_, value]) => value)
      .map(([_, value]) => value)
      .join(" and ");
  };

  const getFilterValue = (key: string, operator: FilterType = "in") => {
    const filter = filters[key] || "";
    if (operator === "like") {
      return filter.split("'")[1];
    }
    if (filter.includes(operator)) {
      return filter.split("(")[1].split(")")[0];
    }
  };

  return {
    searchPhrase,
    filters,
    setSearchPhrase,
    filter: joinNotEmptyFiltersWithAnd(),
    changeFilter,
    initFilters: initFilters.current,
    getInitFilterValue,
    getInitFilterValues,
    updateAllFilters,
    getFilterValue,
  };
}
